import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { EnumEnvs } from '@library/store/config/config.interface';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { endOfScale, startOfScale } from '@library/store/measures/measures';
import { EnumScales } from '@library/store/measures/measures.interface';
import { LocalStorageService } from '@library/utils/services/local-storage.service';
import { ControlPageCompareService } from '@pages/control-page/control-page-main/control-page-compare/control-page-compare.service';
import { CpCompareHeaderCacheService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-header-cache.service';
import { CpCompareScaleService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-scale/cp-compare-scale.service';
import { CpCompareTimeRangeService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-timerange/cp-compare-timerange.service';
import { CpCompareSidebarCardsService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar-cards.service';
import { CpCompareSidebarColorsService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar-colors.service';
import { CpCompareSidebarService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { FeedbackPopupService } from '@pages/control-page/feedback-popup.service';
import * as moment from 'moment';
import { Subscription, firstValueFrom, withLatestFrom } from 'rxjs';
import { CPCompareSidebarCacheService } from './cp-compare-sidebar/cp-compare-sidebar-cache.service';

@Component({
  selector: 'app-control-page-compare',
  templateUrl: './control-page-compare.component.html',
  styleUrls: ['./control-page-compare.component.scss'],
  providers: [
    CpCompareSidebarService,
    CpCompareScaleService,
    CpCompareTimeRangeService,
    ControlPageCompareService,
    CPCompareSidebarCacheService,
    CpCompareSidebarColorsService,
    CpCompareSidebarCardsService,
    CpCompareHeaderCacheService,
  ]
})
export class ControlPageCompareComponent {

  environment = environment;
  EnumEnvs = EnumEnvs;

  sub = new Subscription();

  constructor(
    private controlPageCompareService: ControlPageCompareService,
    private compareScaleService: CpCompareScaleService,
    private compareTimeRangeService: CpCompareTimeRangeService,
    private compareSidebarService: CpCompareSidebarService,
    private homesFacade: HomesFacade,
    private compareHeaderCacheService: CpCompareHeaderCacheService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private feedbackPopupService: FeedbackPopupService,
  ) {}

  ngOnInit() {
    // Load measures of all cards when the timerange is updated
    this.compareTimeRangeService.firstTimeRange$.subscribe(async (timeRange) => {
      const measureCards = await firstValueFrom(this.compareSidebarService.measureCards$);
      this.controlPageCompareService.loadMeasuresForTimeRange(measureCards, timeRange);
    });

    this.compareTimeRangeService.secondTimeRange$.subscribe(async (timeRange) => {
      const measureCards = await firstValueFrom(this.compareSidebarService.measureCards$);
      this.controlPageCompareService.loadMeasuresForTimeRange(measureCards, timeRange);
    });

    // Set scale and TimeRange from cache
    const scale = this.compareHeaderCacheService.scale ?? EnumScales.DAY;
    const secondTimeRange = this.compareHeaderCacheService.secondTimeRange ?? null;

    this.compareScaleService.scale$.next(scale);
    this.setTimeRanges(scale, secondTimeRange);

    // Update the timeranges when the scale changes
    this.compareScaleService.scale$.pipe(
      withLatestFrom(this.compareTimeRangeService.secondTimeRange$)
    ).subscribe(([scale, secondTimeRange]) => {
      this.setTimeRanges(scale, !!secondTimeRange);
    });

    // unselect measrecards when new home is selected
    this.homesFacade.currentHomeId$.subscribe(() => {
      this.compareSidebarService.resetMeasureCards();
    });

    // if never seen, show fus compare
    if (!this.localStorageService.getItem('control-fus-seen-compare')) {
      this.router.navigate(['welcome'], {relativeTo: this.activatedRoute, skipLocationChange: true});
    }

    this.feedbackPopupService.initFeedbackPopup(this.router.url);
  }

  private setTimeRanges(scale: EnumScales, secondTimeRange: boolean) {
    const firstTimeRange = {
      dateBegin: startOfScale(moment(), scale),
      dateEnd: endOfScale(moment(), scale),
    };
    this.compareTimeRangeService.setFirstTimeRange(firstTimeRange);
    if (secondTimeRange) {
      this.compareTimeRangeService.addSecondTimeRange(firstTimeRange, scale);
    }
  }
}
